import * as React from 'react'
import { cn } from '@merels/ui'
import { InfoIcon } from '../../../library/Icons/InfoIcon'
import { FocusNavigationDialog } from '../../../library/FocusNavigationDialog/FocusNavigationDialog'
import { mateListTestIds } from '../../list/MateList/mateList.testIds'

export interface OmniModeLabelProps {
  /** Whether this client may toggle Omni mode. Renders a checkbox when true, plain text otherwise - see OmniModeControls for why only the room owner gets the checkbox. */
  interactive: boolean
  checked: boolean
  onToggle?: (enabled: boolean) => void
}

/**
 * OmniModeLabel renders the "Omni Mode" row at the top of the mates sidebar -
 * an interactive checkbox for whichever client may control it (see
 * OmniModeControls's canUseOmniMode), or plain read-only text for every other
 * connected client while the room is in Omni mode. Either way, an info button
 * opens an explainer dialog, since only the controlling client otherwise has
 * any indication of what Omni mode does.
 */
export function OmniModeLabel({ interactive, checked, onToggle }: OmniModeLabelProps) {
  const [infoOpen, setInfoOpen] = React.useState(false)

  return (
    <div className="px-2 py-2 border-b border-basic flex items-center justify-between gap-1.5" onClick={(e) => e.stopPropagation()}>
      {interactive ? (
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            data-testid="omni-mode-toggle"
            checked={checked}
            onChange={(e) => onToggle?.(e.target.checked)}
            className="w-4 h-4 cursor-pointer"
          />
          <span
            className={cn('text-xs font-medium', !checked && 'text-muted')}
            title="Play as every logical player in this room"
          >
            Omni Mode
          </span>
        </label>
      ) : (
        <span
          data-testid={mateListTestIds.omniModeReadOnlyLabel}
          className="text-xs font-medium"
          title="Play as every logical player in this room"
        >
          Omni Mode
        </span>
      )}

      <FocusNavigationDialog
        open={infoOpen}
        onOpenChange={setInfoOpen}
        title="Omni Mode"
        testId={mateListTestIds.omniModeInfoDialog}
        trigger={
          <button
            type="button"
            aria-label="What is Omni Mode?"
            data-testid={mateListTestIds.omniModeInfoButton}
            className="text-muted hover:text-primary shrink-0"
          >
            <InfoIcon width={14} height={14} />
          </button>
        }
      >
        <div className="text-sm text-muted space-y-2">
          <p>Omni mode allows you to quickly test the game by playing for all players.</p>
          <p>It automatically switches to whichever player's turn it is next, so you can just keep playing without doing anything extra.</p>
          <p>You can also switch to a specific player yourself at any time by pressing the backtick (`) key.</p>
          <p>Only the room's owner can turn Omni mode on or off.</p>
        </div>
      </FocusNavigationDialog>
    </div>
  )
}
